import * as i0 from '@angular/core';
import { forwardRef, EventEmitter, Component, ViewEncapsulation, ChangeDetectionStrategy, Optional, HostBinding, ViewChild, Output, Input, HostListener, NgModule } from '@angular/core';
import * as i1 from '@angular/forms';
import { NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { coerceBooleanProperty, isErrorState } from '@geneplanet/ngx-ui/src/lib/shared';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/naming-convention, @angular-eslint/no-output-native */
const GPU_CHECKBOX_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => GpuCheckboxComponent),
    multi: true,
};
class GpuCheckboxComponent {
    get tabIndex() {
        return !this.disabled ? '0' : '-1';
    }
    get invalid() {
        return this.errorState;
    }
    get checked() {
        return this._checked;
    }
    set checked(value) {
        const newValue = coerceBooleanProperty(value);
        if (newValue != this.checked) {
            this._checked = newValue;
            this.cdRef.markForCheck();
        }
    }
    get disabled() {
        return this._disabled;
    }
    set disabled(value) {
        const newValue = coerceBooleanProperty(value);
        if (newValue !== this.disabled) {
            this._disabled = newValue;
            this.cdRef.markForCheck();
        }
    }
    constructor(cdRef, injector, parentForm, parentFormGroup) {
        this.cdRef = cdRef;
        this.injector = injector;
        this.parentForm = parentForm;
        this.parentFormGroup = parentFormGroup;
        this.checkboxClass = true;
        this.change = new EventEmitter();
        this.focused = false;
        this.errorState = false;
        this._checked = false;
        this._disabled = false;
        this.onChange = () => { };
        this.onTouched = () => { };
    }
    onFocusChanged(focused) {
        if (!focused) {
            this.onTouched();
        }
        if (focused !== this.focused) {
            this.focused = focused;
        }
    }
    toggleChecked(e) {
        if (e) {
            e.preventDefault();
        }
        if (!this._disabled) {
            const checked = !this._checked;
            this.changeValue(checked);
            this.onChange(checked);
            this.onTouched();
        }
    }
    ngOnInit() {
        // Needs to be in onInit - having it in constructor will
        // throw DI recursion error.
        this.ngControl = this.injector.get(NgControl, null);
        if (this.checked) {
            this._checked = this.checked;
        }
        if (this.disabled) {
            this._disabled = this.disabled;
        }
    }
    ngDoCheck() {
        if (this.ngControl) {
            const parent = this.parentFormGroup || this.parentForm;
            this.errorState = isErrorState(this.ngControl?.control, parent);
        }
    }
    writeValue(obj) {
        this.changeValue(obj);
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this._disabled = isDisabled;
    }
    changeValue(checked) {
        this._checked = checked;
        this.change.emit(checked);
        this.cdRef.markForCheck();
        // To make sure it's not out of sync
        if (this._inputElement) {
            this._inputElement.nativeElement.checked = checked;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuCheckboxComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.Injector }, { token: i1.NgForm, optional: true }, { token: i1.FormGroupDirective, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.1.3", type: GpuCheckboxComponent, selector: "gpu-checkbox", inputs: { checked: "checked", disabled: "disabled", value: "value" }, outputs: { change: "change" }, host: { listeners: { "focus": "onFocusChanged(true)", "blur": "onFocusChanged(false)", "click": "toggleChecked()", "keydown.enter": "toggleChecked($event)", "keydown.space": "toggleChecked($event)" }, properties: { "class.gpu-checkbox": "this.checkboxClass", "attr.tabindex": "this.tabIndex", "class.gpu-invalid": "this.invalid", "class.gpu-checked": "this.checked", "class.gpu-disabled": "this.disabled" } }, providers: [GPU_CHECKBOX_ACCESSOR], viewQueries: [{ propertyName: "_inputElement", first: true, predicate: ["input"], descendants: true }], ngImport: i0, template: "<input #input type=\"checkbox\" class=\"gpu-checkbox-input\" [checked]=\"checked\" (click)=\"toggleChecked()\" />\n<div class=\"gpu-icon-container\">\n  <svg class=\"icon\" width=\"24\" height=\"24\" viewBox=\"0 0 24 24\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\">\n    <path\n      class=\"gpu-checkmark\"\n      *ngIf=\"checked\"\n      d=\"M7.83398 11.9999L10.7507 15.3333L16.584 9.08325\"\n      stroke-width=\"2\"\n      stroke-linecap=\"round\"\n      stroke-linejoin=\"round\"\n    />\n    <rect class=\"gpu-checkmark-outline\" x=\"1\" y=\"1\" width=\"22\" height=\"22\" rx=\"7\" stroke-width=\"2\" />\n  </svg>\n</div>\n<ng-content></ng-content>\n", styles: [".gpu-checkbox-input{display:none}.gpu-checkbox{display:flex;justify-content:flex-start;align-items:center;cursor:pointer;-webkit-user-select:none;user-select:none}.gpu-checkbox .gpu-icon-container{display:flex;border-radius:1rem;margin-right:1rem}.gpu-checkbox:hover,.gpu-checkbox:focus,.gpu-checkbox:active{outline:none}.gpu-checkbox:hover .gpu-icon-container,.gpu-checkbox:focus .gpu-icon-container,.gpu-checkbox:active .gpu-icon-container{box-shadow:0 0 0 .5rem #c7c7d21a;background-color:#c7c7d21a}.gpu-checkbox .gpu-checkmark,.gpu-checkbox .gpu-checkmark-outline{stroke:#c7c7d2}.gpu-checkbox.gpu-checked:hover,.gpu-checkbox.gpu-checked:focus,.gpu-checkbox.gpu-checked:active{outline:none}.gpu-checkbox.gpu-checked:hover .gpu-icon-container,.gpu-checkbox.gpu-checked:focus .gpu-icon-container,.gpu-checkbox.gpu-checked:active .gpu-icon-container{box-shadow:0 0 0 .5rem #6a81f61a;background-color:#6a81f61a}.gpu-checkbox.gpu-checked .gpu-checkmark,.gpu-checkbox.gpu-checked .gpu-checkmark-outline{stroke:#6a81f6}.gpu-checkbox.gpu-invalid:hover,.gpu-checkbox.gpu-invalid:focus,.gpu-checkbox.gpu-invalid:active{outline:none}.gpu-checkbox.gpu-invalid:hover .gpu-icon-container,.gpu-checkbox.gpu-invalid:focus .gpu-icon-container,.gpu-checkbox.gpu-invalid:active .gpu-icon-container{box-shadow:0 0 0 .5rem #ff397f1a;background-color:#ff397f1a}.gpu-checkbox.gpu-invalid .gpu-checkmark,.gpu-checkbox.gpu-invalid .gpu-checkmark-outline{stroke:#ff397f}.gpu-checkbox.gpu-disabled{cursor:default}.gpu-checkbox.gpu-disabled:hover,.gpu-checkbox.gpu-disabled:focus,.gpu-checkbox.gpu-disabled:active{outline:none}.gpu-checkbox.gpu-disabled:hover .gpu-icon-container,.gpu-checkbox.gpu-disabled:focus .gpu-icon-container,.gpu-checkbox.gpu-disabled:active .gpu-icon-container{box-shadow:0 0 0 .5rem #e5e5ea1a;background-color:#e5e5ea1a}.gpu-checkbox.gpu-disabled .gpu-checkmark,.gpu-checkbox.gpu-disabled .gpu-checkmark-outline{stroke:#e5e5ea}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuCheckboxComponent, decorators: [{
            type: Component,
            args: [{ selector: 'gpu-checkbox', encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, providers: [GPU_CHECKBOX_ACCESSOR], template: "<input #input type=\"checkbox\" class=\"gpu-checkbox-input\" [checked]=\"checked\" (click)=\"toggleChecked()\" />\n<div class=\"gpu-icon-container\">\n  <svg class=\"icon\" width=\"24\" height=\"24\" viewBox=\"0 0 24 24\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\">\n    <path\n      class=\"gpu-checkmark\"\n      *ngIf=\"checked\"\n      d=\"M7.83398 11.9999L10.7507 15.3333L16.584 9.08325\"\n      stroke-width=\"2\"\n      stroke-linecap=\"round\"\n      stroke-linejoin=\"round\"\n    />\n    <rect class=\"gpu-checkmark-outline\" x=\"1\" y=\"1\" width=\"22\" height=\"22\" rx=\"7\" stroke-width=\"2\" />\n  </svg>\n</div>\n<ng-content></ng-content>\n", styles: [".gpu-checkbox-input{display:none}.gpu-checkbox{display:flex;justify-content:flex-start;align-items:center;cursor:pointer;-webkit-user-select:none;user-select:none}.gpu-checkbox .gpu-icon-container{display:flex;border-radius:1rem;margin-right:1rem}.gpu-checkbox:hover,.gpu-checkbox:focus,.gpu-checkbox:active{outline:none}.gpu-checkbox:hover .gpu-icon-container,.gpu-checkbox:focus .gpu-icon-container,.gpu-checkbox:active .gpu-icon-container{box-shadow:0 0 0 .5rem #c7c7d21a;background-color:#c7c7d21a}.gpu-checkbox .gpu-checkmark,.gpu-checkbox .gpu-checkmark-outline{stroke:#c7c7d2}.gpu-checkbox.gpu-checked:hover,.gpu-checkbox.gpu-checked:focus,.gpu-checkbox.gpu-checked:active{outline:none}.gpu-checkbox.gpu-checked:hover .gpu-icon-container,.gpu-checkbox.gpu-checked:focus .gpu-icon-container,.gpu-checkbox.gpu-checked:active .gpu-icon-container{box-shadow:0 0 0 .5rem #6a81f61a;background-color:#6a81f61a}.gpu-checkbox.gpu-checked .gpu-checkmark,.gpu-checkbox.gpu-checked .gpu-checkmark-outline{stroke:#6a81f6}.gpu-checkbox.gpu-invalid:hover,.gpu-checkbox.gpu-invalid:focus,.gpu-checkbox.gpu-invalid:active{outline:none}.gpu-checkbox.gpu-invalid:hover .gpu-icon-container,.gpu-checkbox.gpu-invalid:focus .gpu-icon-container,.gpu-checkbox.gpu-invalid:active .gpu-icon-container{box-shadow:0 0 0 .5rem #ff397f1a;background-color:#ff397f1a}.gpu-checkbox.gpu-invalid .gpu-checkmark,.gpu-checkbox.gpu-invalid .gpu-checkmark-outline{stroke:#ff397f}.gpu-checkbox.gpu-disabled{cursor:default}.gpu-checkbox.gpu-disabled:hover,.gpu-checkbox.gpu-disabled:focus,.gpu-checkbox.gpu-disabled:active{outline:none}.gpu-checkbox.gpu-disabled:hover .gpu-icon-container,.gpu-checkbox.gpu-disabled:focus .gpu-icon-container,.gpu-checkbox.gpu-disabled:active .gpu-icon-container{box-shadow:0 0 0 .5rem #e5e5ea1a;background-color:#e5e5ea1a}.gpu-checkbox.gpu-disabled .gpu-checkmark,.gpu-checkbox.gpu-disabled .gpu-checkmark-outline{stroke:#e5e5ea}\n"] }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }, { type: i0.Injector }, { type: i1.NgForm, decorators: [{
                    type: Optional
                }] }, { type: i1.FormGroupDirective, decorators: [{
                    type: Optional
                }] }], propDecorators: { checkboxClass: [{
                type: HostBinding,
                args: ['class.gpu-checkbox']
            }], tabIndex: [{
                type: HostBinding,
                args: ['attr.tabindex']
            }], invalid: [{
                type: HostBinding,
                args: ['class.gpu-invalid']
            }], _inputElement: [{
                type: ViewChild,
                args: ['input']
            }], change: [{
                type: Output
            }], checked: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.gpu-checked']
            }], disabled: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.gpu-disabled']
            }], value: [{
                type: Input
            }], onFocusChanged: [{
                type: HostListener,
                args: ['focus', ['true']]
            }, {
                type: HostListener,
                args: ['blur', ['false']]
            }], toggleChecked: [{
                type: HostListener,
                args: ['click']
            }, {
                type: HostListener,
                args: ['keydown.enter', ['$event']]
            }, {
                type: HostListener,
                args: ['keydown.space', ['$event']]
            }] } });

class GpuCheckboxModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuCheckboxModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.1.3", ngImport: i0, type: GpuCheckboxModule, declarations: [GpuCheckboxComponent], imports: [CommonModule], exports: [GpuCheckboxComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuCheckboxModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuCheckboxModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [GpuCheckboxComponent],
                    imports: [CommonModule],
                    exports: [GpuCheckboxComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { GPU_CHECKBOX_ACCESSOR, GpuCheckboxComponent, GpuCheckboxModule };
